import React, { useState, useEffect, useRef, Fragment } from 'react';

import * as uuid from 'uuid';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import FileUploadIcon from '@mui/icons-material/FileUpload';
import CheckIcon from '@mui/icons-material/Check';

import ContentContainer from '../../layout/ContentContainer.jsx';
import request from '@nineeng/request';

import * as authentication from '../../util/authentication.js';
import uploadFile from '../../util/upload';
import { DEFAULT_ACCEPT_STRING, getAcceptedExtensions } from '../../util/acceptedFormats.js';
import { DocumentCamera } from '../../components/DocumentCamera.jsx';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DocumentReviewFeedback from './DocumentReviewFeedback.jsx';
import { buildProblems } from '../../util/documentProblems.js';

const explanationStyle = {
	width: '100%',
	padding: '1.5rem',
	boxSizing: 'border-box',
	margin: '0 auto',
	marginTop: '1rem',
	textAlign: 'left',
};

const documentContainerStyle = {
	paddingTop: '1rem',
	width: '100%',
};

const helperUrls = {
	'nl-notification': 'https://www.postedworkers.nl/',
	'nl-gvva': 'https://www.government.nl/topics/foreign-citizens-working-in-the-netherlands/question-and-answer/what-permits-do-foreign-workers-need',
	'nl-twv': 'https://www.government.nl/topics/foreign-citizens-working-in-the-netherlands/question-and-answer/what-permits-do-foreign-workers-need',
};

const DocUploadLine = ({
	index,
	docTypeId,
	allowedTypes,
	value,
	label,
	name,
	uploadUrl,
	onChange,
	required,
	text,
	useCamera,
	cameraIncludes,
	cameraRotationAngle,	
	minValidityPeriod,
	disabled,
	helperText,
	allowedFormats,
	docTypes,
	context,
	postUploadValidator,
	enableReviewFeedback = false,
	reviewMessages,
	addPendingAction = () => { },
	removePendingAction = () => { },
}) => {
	const [hasError, setError] = useState(null);
	const [hasFeedback, setFeedback] = useState(null);
	const [isLoading, setLoading] = useState(false);
	const [image, setImage] = useState();
	const [showCamera, setShowCamera] = useState(false);
	const [isReviewExpanded, setReviewExpanded] = useState(false);

	const fileInputRef = useRef(null);
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	const handleProceed = () => {
		// reviewAcknowledged: un-blocks "Next" this session AND is persisted to the backend on task
		// submit, so reviewers later see the user was warned and chose to continue anyway.
		onChangeRef.current(index, { ...value, reviewAcknowledged: true });
	};
	const handleCancelReview = () => {
		onChangeRef.current(index, null);
		setError(null);
	};

	const handleFileUpload = async (e) => {
		const file = e.target.files[0];
		await processFileUpload(file, file.type);
	};

	const processFileUpload = async (file, type) => {
		if (isLoading) {
			return;
		}
		setLoading(true);
		setError(null);
		setFeedback(null);
		const action = uuid.v4();
		try {
			addPendingAction(action);

			const uploadParams = {
				contentType: type,
				type: docTypeId || 'generic',
				filename: label,
				context,
				allowedDocumentTypes: allowedTypes,
			};

			const { faceImageBase64, ...uploadedFile } = await uploadFile(uploadUrl, file, false, uploadParams);

			// File comes from ai-api, check if the document is valid, it comes with its own feedback
			if (uploadedFile?.metadata?.validity === false) {
				const feedbackError = uploadedFile?.metadata?.validityFeedback || 'Document validation failed';
				return setError(feedbackError);
			}


			// Type-correctness validation is for legacy (non-review-feedback) flows only.
			if (!enableReviewFeedback) {
				// Handle multi-type validation
				if (allowedTypes) {
					// Check if returned type is in allowed list
					if (!allowedTypes.includes(uploadedFile.type)) {
						const formattedError = text.errorWrongType
							.replace('{expectedType}', allowedTypes.join(' or '))
							.replace('{uploadedFileType}', uploadedFile.type);
						return setError(formattedError);
					}
				} else {
					if (docTypeId !== 'any-id' && uploadedFile?.type !== 'generic' && uploadedFile.type !== docTypeId) {
						const expectedTypeLabel = docTypeId;
						// Format error message with both types
						const formattedError = text.errorWrongType
							.replace('{expectedType}', expectedTypeLabel)
							.replace('{uploadedFileType}', uploadedFile.type);
						return setError(formattedError);
					}

					if (docTypeId === 'any-id' && !['id', 'passport'].includes(uploadedFile.type)) {

						if (uploadedFile.status === 'inReview' && uploadedFile.reviewReason.includes('UNSUPPORTED_CLASS')) {
							return setError(text.errorUnsupportedClass);
						}

						const formattedError = text.errorWrongType.replace('{expectedType}', label || docTypeId);
						return setError(formattedError);
					}
				}
			}

			const maxAcceptedExpiryPeriodDate = minValidityPeriod ? new Date(Date.now() + minValidityPeriod * 24 * 60 * 60 * 1000) : new Date();
			if (uploadedFile?.metadata?.expiryDate && new Date(uploadedFile?.metadata?.expiryDate) < maxAcceptedExpiryPeriodDate) {
				return setError(text.expiredError);
			}
			if (postUploadValidator) {
				const validationError = postUploadValidator(uploadedFile, docTypeId);
				if (validationError) {
					return setError(validationError);
				}
			}

			if (faceImageBase64) setImage(`data:image/png;base64,${faceImageBase64}`);
			if (uploadedFile.status === 'inReview') {
				if (uploadedFile.reviewReason?.includes('UNSUPPORTED_CLASS')) {
					return setError(text.errorUnsupportedClass);
				}
				setFeedback(text.feedbackReview);
			}

			return onChangeRef.current(index, uploadedFile);
		} catch (err) {
			console.error(err);
			onChangeRef.current(index, null);
			setError(err?.message || String(err));
		} finally {
			removePendingAction(action);
			setLoading(false);
		}
	};

	const handleClick = () => {
		if (useCamera) {
			setShowCamera(true);
			return;
		}
		if (!fileInputRef.current) return;
		fileInputRef.current.click();
	};


	const acceptString = allowedFormats ?? DEFAULT_ACCEPT_STRING;
	const acceptedFormats = getAcceptedExtensions(acceptString);

	const FileInput = () => <input ref={fileInputRef} accept={acceptString} style={{ display: 'none' }} type='file' onChange={handleFileUpload} />;

	const {
		countryName = '',
		firstName = '',
		lastName = '',
		dateOfExpiry = '',
	} = value?.metadata || {};



	const isInReview = value?.status === 'inReview';
	const problems = enableReviewFeedback && isInReview ? buildProblems(value, reviewMessages) : [];
	// Single "needs attention" colour — no amber/error gradation. Red only when there's a real
	// worker-facing problem; an inReview doc with none (e.g. defaultInReview / DEFAULT_IN_REVIEW only,
	// which buildProblems excludes) is, to the worker, a normal successful upload → green check.
	const showProblem = isInReview && (!enableReviewFeedback || problems.length > 0);

	if (value && !isLoading) return (
		<div style={{ marginBottom: '1rem' }}>
			<Card variant="outlined" sx={{ '& p': { margin: 0 } }}>
				{image && <img style={{ position: 'absolute', height: 100, borderRadius: 4 }} src={image} />}
				<List disablePadding>
					<ListItem
						button
						onClick={handleClick}
						disabled={disabled}
						sx={{ height: 100 }}
					>
						{!value.faceImage && <ListItemIcon sx={{ minWidth: 80 }}>
							{showProblem ? <WarningAmberIcon sx={{ color: 'error.main' }} /> : <CheckIcon />}
						</ListItemIcon>}
						<ListItemText
							sx={{ paddingLeft: value.faceImage ? '80px' : 0 }}
							primary={`${label} ${required ? '*' : ''}`}
							primaryTypographyProps={{ color: showProblem ? 'error.main' : 'green' }}
							secondary={name && <Fragment>
								<p>{firstName} {lastName}</p>
								{countryName && <p>{text.lblCountry}: {countryName}</p>}
								{dateOfExpiry && <p>{text.lblExpires}: {new Date(dateOfExpiry).toLocaleDateString()}</p>}
								{showProblem && hasFeedback && <p>{hasFeedback}</p>}
							</Fragment>}
						/>
					</ListItem>
					{problems.length > 0 && (
						<DocumentReviewFeedback
							problems={problems}
							copy={reviewMessages}
							acknowledged={!!value.reviewAcknowledged}
							expanded={isReviewExpanded}
							onToggle={() => setReviewExpanded((v) => !v)}
							onConfirm={handleProceed}
							onCancel={handleCancelReview}
						/>
					)}
				</List>
			</Card>
			{helperText && helperUrls[docTypeId] && (
				<Typography variant='caption' sx={{ marginTop: 2, cursor: 'pointer', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '100%', display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
					{helperText + ': '}
					<Link sx={{ color: 'inherit', '&:hover': { color: 'rgba(0, 0, 0, 0.6)' } }} href={helperUrls[docTypeId]} target='_blank'>{helperUrls[docTypeId]}</Link>
				</Typography>)}
			{
				useCamera ?
					showCamera && <DocumentCamera
						title={label}
						cameraIncludes={cameraIncludes}
						cameraRotationAngle={cameraRotationAngle}
						onClose={() => setShowCamera(false)}
						onImage={handleFileUpload}
						onLoading={() => setLoading(true)}
					/>
					:
					<FileInput />
			}
		</div>
	);

	return (
		<div style={{ marginBottom: '1rem' }}>
			<Card variant="outlined">
				<List disablePadding>
					<ListItem
						button
						onClick={handleClick}
						disabled={isLoading || disabled}
						sx={{ minHeight: 100 }}
					>
						<ListItemIcon sx={{ minWidth: 80 }}>
							{isLoading ? <CircularProgress /> : <FileUploadIcon />}
						</ListItemIcon>
						<ListItemText
							primary={`${label} ${required ? '*' : ''}`}
							secondary={hasError
								|| (acceptedFormats.length > 0
									? `${text.lblUploadOrCapture} (${acceptedFormats.join(', ')})`
									: text.lblUploadOrCapture)}
							secondaryTypographyProps={
								hasError
									? { color: 'red', sx: { whiteSpace: 'pre-line' } }
									: {}
							}
						/>
					</ListItem>
				</List>
			</Card>
			{helperText && helperUrls[docTypeId] && (
				<Typography variant='caption' sx={{ marginTop: 1, cursor: 'pointer', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', maxWidth: '100%', display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
					{helperText + ': '}
					<Link sx={{ color: 'inherit', '&:hover': { color: 'rgba(0, 0, 0, 0.6)' } }} href={helperUrls[docTypeId]} target='_blank'>{helperUrls[docTypeId]}</Link>
				</Typography>
			)}
			{
				useCamera ?
					showCamera && <DocumentCamera
						title={label}
						cameraIncludes={cameraIncludes}
						cameraRotationAngle={cameraRotationAngle}
						onClose={() => setShowCamera(false)}
						onImage={handleFileUpload}
						onLoading={() => setLoading(true)}
					/>
					:
					<FileInput />
			}
		</div>
	);
};

const ScanningPage = ({
	possibleDocuments = [],
	requiredDocuments = [],
	uploadUrl,
	onChange,
	addPendingAction,
	removePendingAction,
	minValidityPeriod,
	value = [],
	labels,
	helperTexts,
	text,
	explainer,
	handleRequestException,
	exceptionText,
	useCamera,
	cameraIncludes,
	cameraRotationAngle,
	disabled,
	helperText,
	context,
	documentTypeForMultipleTypes,
	postUploadValidator,
	enableReviewFeedback = false,
	reviewMessages,
}) => {
	const [documentTypes, setDocumentTypes] = useState();
	const [loading, setLoading] = useState(true);
	useEffect(() => {
		const fetchDocumentTypes = async () => {
			try {
				const authToken = authentication.extractAuthorization();
				const authParams = authentication.extractAuthParams();
				const { body } = await request.get(`/api/documentTypes?pagesize=0&${authParams}`, {
					headers: { Authorization: authToken },
				});
				if (body) {
					setDocumentTypes(body.data ?? []);
				} else {
					setDocumentTypes([]);
				}
			} catch (err) {
				setDocumentTypes([]);
			} finally {
				setLoading(false);
			}
		};

		if (!documentTypes) {
			fetchDocumentTypes();
		}
	}, []);

	const docMapper = (doc, required) => {
		// Handle multi-type documents (array of document types)
		if (Array.isArray(doc)) {
			const allowedTypes = doc;
			const compositeId = doc.join(','); // e.g., 'residence-permit,bijlage-3'

			if (!documentTypeForMultipleTypes) {
				console.error('documentTypeForMultipleTypes is not set');
			}
			// As this is a multi-type document, we need the unknown-user-document-type
			const documentType = documentTypes?.find((docType) => docType.id === documentTypeForMultipleTypes);

			return {
				id: documentType?.id,
				allowedTypes, // Store the array for validation
				compositeId,
				required,
				label: labels[compositeId] || doc.join(' or '),
				allowedFormats: documentType?.allowedFormats,
				parsingMethod: documentType?.parsingMethod,
			};
		}

		// Single-type document (original logic)
		const documentType = documentTypes?.find((docType) => docType.id === doc);
		return {
			id: doc,
			compositeId: doc,
			required,
			label: documentType?.name,
			allowedFormats: documentType?.allowedFormats,
			parsingMethod: documentType?.parsingMethod,
		};
	};

	const docs = [
		...requiredDocuments.map((doc) => docMapper(doc, true)),
		...possibleDocuments.filter(possibleDoc =>
			!requiredDocuments.some(requiredDoc => possibleDoc === requiredDoc))
			.map((doc) => docMapper(doc, false)),
	];

	return (
		<ContentContainer innerStyle={{ padding: 0, width: '100%' }}>
			<div style={explanationStyle}>
				{explainer}
				<div style={documentContainerStyle}>
					{docs.map((doc, i) => (
						<>
							<DocUploadLine
								index={i}
								docTypeId={doc.id}
								allowedTypes={doc.allowedTypes}
								key={doc.id}
								minValidityPeriod={minValidityPeriod}
								onChange={onChange}
								uploadUrl={uploadUrl}
								addPendingAction={addPendingAction}
								removePendingAction={removePendingAction}
								value={value[i]}
								label={labels[doc.id] || doc.label || doc.id}
								name={doc.label}
								required={doc.required}
								text={text}
								// useCamera only takes effect when a device header is present (deviceId= in the URL);
								// on regular (non-kiosk) sessions it has no impact.
								useCamera={useCamera && window.location.href.includes('deviceId=')}
								cameraIncludes={cameraIncludes}
								cameraRotationAngle={cameraRotationAngle}
								disabled={disabled || loading}
								helperText={helperText}
								allowedFormats={doc.allowedFormats}
								parsingMethod={doc.parsingMethod}
								docTypes={documentTypes}
								context={context}
								postUploadValidator={postUploadValidator}
								enableReviewFeedback={enableReviewFeedback}
								reviewMessages={reviewMessages}
							/>
							{doc.compositeId && helperTexts?.[doc.compositeId] && (
								<Typography 
									variant='caption' 
									sx={{ marginBottom: 2, marginLeft: '2rem', marginTop: 1, display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
									{helperTexts[doc.compositeId]}
								</Typography>
							)}
						</>
					))}
				</div>
			</div>
			{handleRequestException &&
				(docs.flatMap(document => document.id).includes('limosa') || docs.flatMap(document => document.id).includes('proof-of-independence')) &&
					<Typography
						variant='caption'
						sx={{ marginBottom: 2, textDecoration: 'underline', cursor: 'pointer', marginLeft: '2rem' }}
						onClick={handleRequestException}>
						{exceptionText || text.requestException}
					</Typography>
			}
		</ContentContainer>
	);
};

export default ScanningPage;
